import React from 'react';
import { connect } from 'react-redux';
import { get, post, toWapi } from '../../util/api';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Chip,
	MenuItem,
	Popover,
	Select,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import MemoryIcon from '@mui/icons-material/Memory';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DownloadIcon from '@mui/icons-material/Download';
import Page from '../../components/Page';
import CodeIcon from '@mui/icons-material/Code';
import * as XLSX from 'xlsx';

function parseMemoryMB(str) {
	if (!str || typeof str !== 'string') return null;
	const result = {};
	str.split(',').forEach((part) => {
		const m = part.trim().match(/^(\w+)=([\d.]+)\s*(GB|MB|KB|B)?/);
		if (m) {
			const val = parseFloat(m[2]);
			const unit = m[3] || 'MB';
			let mb = val;
			if (unit === 'GB') mb = val * 1024;
			else if (unit === 'KB') mb = val / 1024;
			else if (unit === 'B') mb = val / (1024 * 1024);
			result[m[1]] = Math.round(mb * 100) / 100;
		}
	});
	return Object.keys(result).length > 0 ? result : null;
}

function parseMBInput(val) {
	if (!val) return null;
	const m = String(val)
		.trim()
		.match(/^([\d.]+)\s*(GB|MB|KB)?$/i);
	if (!m) return null;
	const n = parseFloat(m[1]);
	const u = (m[2] || 'GB').toUpperCase();
	if (u === 'GB') return n * 1024;
	if (u === 'KB') return n / 1024;
	return n;
}

function fmtMB(mb) {
	if (mb >= 1024) return (mb / 1024).toFixed(2) + ' GB';
	return mb.toFixed(0) + ' MB';
}

function timeAgo(date) {
	if (!date) return '—';
	const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
	if (seconds < 60) return `${seconds}s ago`;
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h ago`;
	const days = Math.floor(hours / 24);
	return `${days}d ago`;
}

function getColor(totalMB) {
	const gb = totalMB / 1024;
	if (gb <= 3) return { bg: '#e8f5e9', text: '#2e7d32', label: 'Normal' };
	if (gb <= 7) return { bg: '#fff9c4', text: '#f57f17', label: 'High' };
	if (gb <= 10) return { bg: '#ffe0b2', text: '#e65100', label: 'Very High' };
	return { bg: '#ffebee', text: '#c62828', label: 'Critical' };
}

function PhoneRow({ row, index, queue, queueLoading, lastMsg, lastMsgLoading, lastScan, lastScanLoading, version, mem, onClear, pid, token }) {
	const [clearing, setClearing] = React.useState(false);
	const [clearDone, setClearDone] = React.useState(false);
	const [confirm, setConfirm] = React.useState(false);
	const [redeploying, setRedeploying] = React.useState(false);
	const [redeployConfirm, setRedeployConfirm] = React.useState(false);

	async function handleRedeploy() {
		if (!redeployConfirm) { setRedeployConfirm(true); return; }
		setRedeployConfirm(false);
		setRedeploying(true);
		try {
			await toWapi(`/api/${pid}/${row.phoneId}/redeploy`, token, {});
		} finally {
			setRedeploying(false);
		}
	}
	const [statusAnchor, setStatusAnchor] = React.useState(null);
	const [statusData, setStatusData] = React.useState(null);
	const [statusLoading, setStatusLoading] = React.useState(false);

	function handleStatus(e) {
		setStatusAnchor(e.currentTarget);
		if (statusData) return;
		setStatusLoading(true);
		toWapi(`/api/${pid}/${row.phoneId}/status`, token)
			.then((res) => setStatusData(res && res.data))
			.catch(() => setStatusData({ error: 'Failed' }))
			.finally(() => setStatusLoading(false));
	}

	async function handleClear() {
		if (!confirm) {
			setConfirm(true);
			return;
		}
		setConfirm(false);
		setClearing(true);
		try {
			await onClear(row.phoneId);
			setClearDone(true);
		} finally {
			setClearing(false);
		}
	}

	const color = mem?.total != null ? getColor(mem.total) : null;

	return (
		<TableRow sx={{ backgroundColor: color ? color.bg : 'inherit' }}>
			<TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary' }}>{index}</TableCell>
			<TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.phoneId}</TableCell>
			<TableCell>
				{color && (
					<Chip
						label={color.label}
						size="small"
						sx={{ backgroundColor: color.bg, color: color.text, fontWeight: 'bold', border: `1px solid ${color.text}`, fontSize: 11 }}
					/>
				)}
			</TableCell>
			<TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{mem?.total != null ? fmtMB(mem.total) : '—'}</TableCell>
			<TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{mem?.sessionData != null ? fmtMB(mem.sessionData) : '—'}</TableCell>
			<TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{mem?.other != null ? fmtMB(mem.other) : '—'}</TableCell>
			<TableCell sx={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>
				{version ? (
					<>
						{version.api_version || '—'}
						<span style={{ marginLeft: 4, color: version.lastVersion ? '#2e7d32' : '#c62828' }}>
							({version.lastVersion ? '✓' : '✗'})
						</span>
					</>
				) : '—'}
			</TableCell>
			<TableCell sx={{ fontFamily: 'monospace', fontSize: 12, textAlign: 'center' }}>
				{queueLoading ? <CircularProgress size={10} /> : queue != null ? queue : '—'}
			</TableCell>
			<TableCell
				sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary' }}
				title={lastMsg ? lastMsg.toISOString().replace('T', ' ').slice(0, 19) + ' UTC' : ''}
			>
				{lastMsgLoading ? <CircularProgress size={10} /> : timeAgo(lastMsg)}
			</TableCell>
			<TableCell
				sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary' }}
				title={mem?.ts ? mem.ts.toISOString().replace('T', ' ').slice(0, 19) + ' UTC' : ''}
			>
				{timeAgo(mem?.ts)}
			</TableCell>
			<TableCell
				sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary' }}
				title={lastScan ? lastScan.toISOString().replace('T', ' ').slice(0, 19) + ' UTC' : ''}
			>
				{lastScanLoading ? <CircularProgress size={10} /> : timeAgo(lastScan)}
			</TableCell>
			<TableCell>
				<Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
					<Button size="small" variant="outlined" onClick={handleStatus} sx={{ textTransform: 'none', fontSize: 11 }}>
						Status
					</Button>
					<Popover
						open={Boolean(statusAnchor)}
						anchorEl={statusAnchor}
						onClose={() => setStatusAnchor(null)}
						anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
					>
						<Box sx={{ p: 1.5, maxWidth: 320, maxHeight: 300, overflow: 'auto' }}>
							{statusLoading ? (
								<CircularProgress size={16} />
							) : (
								<Typography
									variant="caption"
									component="pre"
									sx={{ fontFamily: 'monospace', fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
								>
									{JSON.stringify(statusData, null, 2)}
								</Typography>
							)}
						</Box>
					</Popover>
					<Button
						size="small"
						variant="outlined"
						component="a"
						href={`https://api.maytapi.com/api/${pid}/${row.phoneId}/screen?token=${token}&time=`}
						target="_blank"
						rel="noopener noreferrer"
						sx={{ textTransform: 'none', fontSize: 11 }}
					>
						Screen
					</Button>
					{redeployConfirm ? (
							<>
								<Typography variant="caption" color="error">Emin misin?</Typography>
								<Button size="small" variant="contained" color="error" disabled={redeploying} onClick={handleRedeploy} sx={{ textTransform: 'none', fontSize: 11 }}>
									{redeploying ? <CircularProgress size={12} /> : 'Evet'}
								</Button>
								<Button size="small" variant="outlined" onClick={() => setRedeployConfirm(false)} sx={{ textTransform: 'none', fontSize: 11 }}>İptal</Button>
							</>
						) : (
							<Button size="small" variant="contained" disabled={redeploying} onClick={handleRedeploy} color="warning" sx={{ textTransform: 'none', fontSize: 11, boxShadow: 'none' }}>
								Redeploy
							</Button>
						)}
					{clearDone ? (
						<>
							<Chip label="Cleared" color="success" size="small" />
							<Button size="small" variant="outlined" color="info" href={`/phones`} sx={{ textTransform: 'none', fontSize: 11 }}>
								Scan QR
							</Button>
						</>
					) : confirm ? (
						<>
							<Typography variant="caption" color="error">
								Are you sure?
							</Typography>
							<Button
								size="small"
								variant="contained"
								color="error"
								disabled={clearing}
								onClick={handleClear}
								sx={{ textTransform: 'none', fontSize: 11 }}
							>
								{clearing ? <CircularProgress size={12} /> : 'Yes, Clear'}
							</Button>
							<Button size="small" variant="outlined" onClick={() => setConfirm(false)} sx={{ textTransform: 'none', fontSize: 11 }}>
								Cancel
							</Button>
						</>
					) : (
						<Button size="small" variant="contained" onClick={handleClear} color="error" sx={{ textTransform: 'none', fontSize: 11, boxShadow: 'none' }}>
							Clear
						</Button>
					)}
				</Box>
			</TableCell>
		</TableRow>
	);
}

function toUtcLocal(date) {
	return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

const MEM_KEYS = ['total', 'sessionData', 'other'];

const TIME_FILTER_KEYS = [
	{ key: 'lastMsg', label: 'last msg' },
	{ key: 'lastScan', label: 'last scan' },
	{ key: 'ts', label: 'last size' },
];

function parseTimeMs(val, unit) {
	const n = parseFloat(val);
	if (!val || isNaN(n) || n <= 0) return null;
	if (unit === 'm') return n * 60 * 1000;
	if (unit === 'h') return n * 60 * 60 * 1000;
	if (unit === 'd') return n * 24 * 60 * 60 * 1000;
	return null;
}

function InstanceSizesPage({ product }) {
	const [loading, setLoading] = React.useState(false);
	const [rows, setRows] = React.useState(null);
	const [memMap, setMemMap] = React.useState({});
	const [page, setPage] = React.useState(0);
	const [pageSize, setPageSize] = React.useState(100);
	const [queueMap, setQueueMap] = React.useState({});
	const [queueLoading, setQueueLoading] = React.useState(false);
	const [lastMsgMap, setLastMsgMap] = React.useState({});
	const [lastMsgLoading, setLastMsgLoading] = React.useState(false);
	const [lastScanMap, setLastScanMap] = React.useState({});
	const [lastScanLoading, setLastScanLoading] = React.useState(false);
	const [versionMap, setVersionMap] = React.useState({});
	const [error, setError] = React.useState(null);
	const [filter, setFilter] = React.useState('');
	const [memFilters, setMemFilters] = React.useState({});
	const [queueFilter, setQueueFilter] = React.useState({ op: '>', val: '' });
	const [versionFilter, setVersionFilter] = React.useState({ apiVersion: '', lastVersion: '' });
	const [timeFilters, setTimeFilters] = React.useState({});
	const [sortKey, setSortKey] = React.useState('total');
	const [sortDir, setSortDir] = React.useState('desc');

	const initEnd = React.useMemo(() => toUtcLocal(new Date()), []);
	const initStart = React.useMemo(() => toUtcLocal(new Date(Date.now() - 2 * 60 * 60 * 1000)), []);
	const [rangeStart, setRangeStart] = React.useState(initStart);
	const [rangeEnd, setRangeEnd] = React.useState(initEnd);

	function load() {
		setLoading(true);
		setError(null);
		setQueueLoading(true);
		setQueueMap({});
		setLastMsgLoading(true);
		setLastMsgMap({});
		setLastScanLoading(true);
		setLastScanMap({});
		setMemMap({});
		const params = `?rangeStart=${encodeURIComponent(new Date(rangeStart).toISOString())}&rangeEnd=${encodeURIComponent(new Date(rangeEnd).toISOString())}`;
		const pid = product && ((product.settings && product.settings.wapi_pid) || product.id);
		const token = product && product.settings && product.settings.wapi_token;

		let pending = 2;
		function checkDone() { if (--pending === 0) setLoading(false); }

		// Base: listPhones
		if (pid && token) {
			toWapi(`/api/${pid}/listPhones`, token)
				.then((res) => {
					const phones = Array.isArray(res.data) ? res.data : res.data && res.data.data;
					if (phones) {
						setRows(phones.map((p) => ({ phoneId: p.id })));
						const map = {};
						phones.forEach((p) => {
							map[p.id] = { api_version: p.api_version, lastVersion: p.lastVersion };
						});
						setVersionMap(map);
					} else {
						setRows([]);
					}
				})
				.catch((e) => { setError(e.message); setRows([]); })
				.finally(() => checkDone());

			toWapi(`/api/${pid}/queues`, token)
				.then((queueRes) => {
					if (queueRes && queueRes.data && queueRes.data.queues) {
						const map = {};
						queueRes.data.queues.forEach((q) => {
							map[q.id] = q.queue;
						});
						setQueueMap(map);
					}
				})
				.catch(() => {})
				.finally(() => setQueueLoading(false));
		} else {
			checkDone();
			setQueueLoading(false);
			setRows([]);
		}

		// instanceSizes → memMap
		get(`/maytapi/instanceSizes${params}`)
			.then((data) => {
				if (data.success) {
					const map = {};
					(data.rows || []).forEach((row) => {
						const arr = Array.isArray(row) ? row : Object.values(row);
						const phoneId = arr.find((v) => typeof v === 'number' && v > 100 && v < 1e15);
						const tsTicks = arr.find((v) => typeof v === 'number' && v > 6e17);
						const msgStr = arr.find((v) => typeof v === 'string' && v.includes('total='));
						const mem = parseMemoryMB(msgStr);
						if (!phoneId || !mem) return;
						const ts = tsTicks ? new Date((tsTicks - 621355968000000000) / 10000) : null;
						map[phoneId] = { ts, ...mem };
					});
					setMemMap(map);
				}
			})
			.catch(() => {})
			.finally(() => checkDone());

		get(`/maytapi/lastMessages${params}`)
			.then((data) => {
				if (data.success) {
					const map = {};
					(data.rows || []).forEach((row) => {
						const arr = Array.isArray(row) ? row : Object.values(row);
						const phoneId = arr.find((v) => typeof v === 'number' && v > 100 && v < 1e15);
						const tsTicks = arr.find((v) => typeof v === 'number' && v > 6e17);
						if (phoneId && tsTicks) {
							map[phoneId] = new Date((tsTicks - 621355968000000000) / 10000);
						}
					});
					setLastMsgMap(map);
				}
			})
			.catch(() => {})
			.finally(() => setLastMsgLoading(false));

		get(`/maytapi/lastQrScan`)
			.then((data) => {
				if (data.success) {
					const map = {};
					(data.rows || []).forEach((row) => {
						const arr = Array.isArray(row) ? row : Object.values(row);
						const phoneId = arr.find((v) => typeof v === 'number' && v > 100 && v < 1e15);
						const tsTicks = arr.find((v) => typeof v === 'number' && v > 6e17);
						if (phoneId && tsTicks) {
							const date = new Date((tsTicks - 621355968000000000) / 10000);
							if (!map[phoneId] || date > map[phoneId]) {
								map[phoneId] = date;
							}
						}
					});
					setLastScanMap(map);
				}
			})
			.catch(() => {})
			.finally(() => setLastScanLoading(false));
	}

	React.useEffect(() => {
		load();
	}, []);

	function setMemFilter(key, field, value) {
		setPage(0);
		setMemFilters((prev) => ({ ...prev, [key]: { op: '>', val: '', ...prev[key], [field]: value } }));
	}

	function clearMemFilter(key) {
		setPage(0);
		setMemFilters((prev) => {
			const n = { ...prev };
			delete n[key];
			return n;
		});
	}

	function setTimeFilter(key, field, value) {
		setPage(0);
		setTimeFilters((prev) => ({ ...prev, [key]: { op: '>', val: '', unit: 'h', ...prev[key], [field]: value } }));
	}

	function clearTimeFilter(key) {
		setPage(0);
		setTimeFilters((prev) => {
			const n = { ...prev };
			delete n[key];
			return n;
		});
	}

	function toggleSort(key) {
		setPage(0);
		if (sortKey === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
		else {
			setSortKey(key);
			setSortDir('desc');
		}
	}

	async function handleClear(phoneId) {
		await post(`/maytapi/phone/${phoneId}/clear`, {});
	}

	function handleDownload() {
		const sheetData = displayed.map((r, i) => {
			const mem = memMap[r.phoneId];
			const ver = versionMap[r.phoneId];
			const lastMsg = lastMsgMap[r.phoneId];
			const lastScan = lastScanMap[r.phoneId];
			return {
				'#': i + 1,
				'Phone ID': r.phoneId,
				'Memory': mem?.total != null ? getColor(mem.total).label : '',
				'Total (MB)': mem?.total != null ? Math.round(mem.total * 100) / 100 : '',
				'Total (GB)': mem?.total != null ? Math.round((mem.total / 1024) * 100) / 100 : '',
				'Session Data (MB)': mem?.sessionData != null ? Math.round(mem.sessionData * 100) / 100 : '',
				'Other (MB)': mem?.other != null ? Math.round(mem.other * 100) / 100 : '',
				'API Version': ver?.api_version || '',
				'Last Version': ver?.lastVersion != null ? (ver.lastVersion ? 'Yes' : 'No') : '',
				'Queue': queueMap[r.phoneId] != null ? queueMap[r.phoneId] : '',
				'Last Msg': lastMsg ? lastMsg.toISOString().replace('T', ' ').slice(0, 19) + ' UTC' : '',
				'Last Size': mem?.ts ? mem.ts.toISOString().replace('T', ' ').slice(0, 19) + ' UTC' : '',
				'Last Scan': lastScan ? lastScan.toISOString().replace('T', ' ').slice(0, 19) + ' UTC' : '',
			};
		});
		const ws = XLSX.utils.json_to_sheet(sheetData);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, 'Instance Status');
		const dateStr = new Date().toISOString().slice(0, 16).replace('T', '_').replace(':', '-');
		XLSX.writeFile(wb, `instance-status_${dateStr}.xlsx`);
	}

	const displayed = React.useMemo(() => {
		if (!rows) return [];
		let list = rows.filter((r) => !filter || String(r.phoneId).includes(filter));
		Object.entries(memFilters).forEach(([key, { op, val }]) => {
			const threshold = parseMBInput(val);
			if (threshold === null) return;
			list = list.filter((r) => {
				const v = memMap[r.phoneId]?.[key] ?? 0;
				if (op === '>') return v > threshold;
				if (op === '<') return v < threshold;
				return Math.abs(v - threshold) < 1;
			});
		});
		if (queueFilter.val !== '') {
			const threshold = parseFloat(queueFilter.val);
			if (!isNaN(threshold)) {
				list = list.filter((r) => {
					const v = queueMap[r.phoneId] ?? 0;
					if (queueFilter.op === '>') return v > threshold;
					if (queueFilter.op === '<') return v < threshold;
					return v === threshold;
				});
			}
		}
		if (versionFilter.apiVersion) {
			list = list.filter((r) => {
				const v = versionMap[r.phoneId];
				return v && (v.api_version || '').includes(versionFilter.apiVersion);
			});
		}
		if (versionFilter.lastVersion !== '') {
			const expected = versionFilter.lastVersion === 'true';
			list = list.filter((r) => {
				const v = versionMap[r.phoneId];
				return v && Boolean(v.lastVersion) === expected;
			});
		}
		// Time filters: ">" means "older than X" (age > threshold), "<" means "newer than X"
		Object.entries(timeFilters).forEach(([key, { op, val, unit }]) => {
			const ms = parseTimeMs(val, unit);
			if (ms === null) return;
			const cutoff = Date.now() - ms;
			list = list.filter((r) => {
				let date;
				if (key === 'lastMsg') date = lastMsgMap[r.phoneId];
				else if (key === 'lastScan') date = lastScanMap[r.phoneId];
				else if (key === 'ts') date = memMap[r.phoneId]?.ts;
				if (!date) return op === '>'; // no date → infinitely old, passes ">" filter
				const t = date.getTime();
				if (op === '>') return t < cutoff;  // older than threshold
				if (op === '<') return t > cutoff;  // newer than threshold
				return false;
			});
		});
		list = [...list].sort((a, b) => {
			const av = sortKey === 'queue' ? queueMap[a.phoneId] ?? 0 : memMap[a.phoneId]?.[sortKey] ?? 0;
			const bv = sortKey === 'queue' ? queueMap[b.phoneId] ?? 0 : memMap[b.phoneId]?.[sortKey] ?? 0;
			return sortDir === 'desc' ? bv - av : av - bv;
		});
		return list;
	}, [rows, memMap, filter, memFilters, queueFilter, queueMap, versionFilter, versionMap, timeFilters, lastMsgMap, lastScanMap, sortKey, sortDir]);

	const totalPages = Math.ceil(displayed.length / pageSize);
	const paged = displayed.slice(page * pageSize, (page + 1) * pageSize);

	const stats = React.useMemo(() => {
		if (!rows) return null;
		return {
			normal: rows.filter((r) => (memMap[r.phoneId]?.total || 0) / 1024 <= 3).length,
			high: rows.filter((r) => {
				const g = (memMap[r.phoneId]?.total || 0) / 1024;
				return g > 3 && g <= 7;
			}).length,
			vhigh: rows.filter((r) => {
				const g = (memMap[r.phoneId]?.total || 0) / 1024;
				return g > 7 && g <= 10;
			}).length,
			critical: rows.filter((r) => (memMap[r.phoneId]?.total || 0) / 1024 > 10).length,
		};
	}, [rows, memMap]);

	return (
		<Page title="Developers" subtitle="Instance Status" icon={<CodeIcon />}>
			<Box sx={{ p: 2 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
					<MemoryIcon color="primary" />
					<Typography variant="h6">Instance Status</Typography>
					<Box sx={{ flex: 1 }} />
					<Button
						size="small"
						variant="outlined"
						startIcon={<DownloadIcon />}
						onClick={handleDownload}
						disabled={!displayed.length}
						sx={{ textTransform: 'none' }}
					>
						Excel ({displayed.length})
					</Button>
					<Button
						size="small"
						variant="outlined"
						startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
						onClick={load}
						disabled={loading}
						sx={{ textTransform: 'none' }}
					>
						Refresh
					</Button>
				</Box>

				<Box sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
					<TextField
						label="Start (UTC)"
						type="datetime-local"
						size="small"
						value={rangeStart}
						onChange={(e) => setRangeStart(e.target.value)}
						InputLabelProps={{ shrink: true }}
						sx={{ width: 210 }}
					/>
					<TextField
						label="End (UTC)"
						type="datetime-local"
						size="small"
						value={rangeEnd}
						onChange={(e) => setRangeEnd(e.target.value)}
						InputLabelProps={{ shrink: true }}
						sx={{ width: 210 }}
					/>
					<Button
						size="small"
						variant="contained"
						onClick={load}
						disabled={loading}
						startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
					>
						Load
					</Button>
				</Box>

				{stats && (
					<Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
						{[
							{ label: `Normal (≤3 GB): ${stats.normal}`, op: '<', val: '3 GB', bg: '#e8f5e9', text: '#2e7d32' },
							{ label: `High (>3 GB): ${stats.high}`, op: '>', val: '3 GB', bg: '#fff9c4', text: '#f57f17' },
							{ label: `Very High (>7 GB): ${stats.vhigh}`, op: '>', val: '7 GB', bg: '#ffe0b2', text: '#e65100' },
							{ label: `Critical (>10 GB): ${stats.critical}`, op: '>', val: '10 GB', bg: '#ffebee', text: '#c62828' },
						].map(({ label, op, val, bg, text }) => {
							const active = memFilters.total && memFilters.total.op === op && memFilters.total.val === val;
							return (
								<Chip
									key={val + op}
									label={label}
									size="small"
									onClick={() => (active ? clearMemFilter('total') : setMemFilters((prev) => ({ ...prev, total: { op, val } })))}
									sx={{
										backgroundColor: bg,
										color: text,
										fontWeight: 'bold',
										cursor: 'pointer',
										border: active ? `2px solid ${text}` : '2px solid transparent',
									}}
								/>
							);
						})}
					</Box>
				)}

				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-end' }}>
					<TextField
						size="small"
						placeholder="Filter by phone ID"
						value={filter}
						onChange={(e) => setFilter(e.target.value)}
						sx={{ width: 200 }}
						InputLabelProps={{ shrink: true }}
						label="Phone ID"
					/>
					{MEM_KEYS.map((k) => {
						const f = memFilters[k] || { op: '>', val: '' };
						const active = f.val !== '';
						return (
							<Box
								key={k}
								sx={{
									display: 'flex',
									alignItems: 'center',
									border: '1px solid',
									borderColor: active ? 'primary.main' : 'divider',
									borderRadius: 1,
									px: 0.5,
								}}
							>
								<Typography variant="caption" sx={{ mr: 0.5, fontWeight: 'bold', color: active ? 'primary.main' : 'text.secondary' }}>
									{k}
								</Typography>
								<Select
									size="small"
									value={f.op}
									onChange={(e) => setMemFilter(k, 'op', e.target.value)}
									sx={{ fontSize: 12, height: 28, '.MuiOutlinedInput-notchedOutline': { border: 'none' }, minWidth: 42 }}
								>
									<MenuItem value=">">{'>'}</MenuItem>
									<MenuItem value="<">{'<'}</MenuItem>
									<MenuItem value="=">{'='}</MenuItem>
								</Select>
								<TextField
									size="small"
									placeholder="1.5 GB"
									value={f.val}
									onChange={(e) => setMemFilter(k, 'val', e.target.value)}
									sx={{
										width: 80,
										'& .MuiInputBase-input': { fontSize: 12, py: 0.5, px: 0.5 },
										'& .MuiOutlinedInput-notchedOutline': { border: 'none' },
									}}
								/>
								{active && (
									<Button size="small" onClick={() => clearMemFilter(k)} sx={{ minWidth: 0, p: 0.2, fontSize: 11, color: 'text.secondary' }}>
										✕
									</Button>
								)}
							</Box>
						);
					})}
					{/* Queue filter */}
					{(() => {
						const active = queueFilter.val !== '';
						return (
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									border: '1px solid',
									borderColor: active ? 'primary.main' : 'divider',
									borderRadius: 1,
									px: 0.5,
								}}
							>
								<Typography variant="caption" sx={{ mr: 0.5, fontWeight: 'bold', color: active ? 'primary.main' : 'text.secondary' }}>
									queue
								</Typography>
								<Select
									size="small"
									value={queueFilter.op}
									onChange={(e) => setQueueFilter((f) => ({ ...f, op: e.target.value }))}
									sx={{ fontSize: 12, height: 28, '.MuiOutlinedInput-notchedOutline': { border: 'none' }, minWidth: 42 }}
								>
									<MenuItem value=">">{'>'}</MenuItem>
									<MenuItem value="<">{'<'}</MenuItem>
									<MenuItem value="=">{'='}</MenuItem>
								</Select>
								<TextField
									size="small"
									placeholder="0"
									value={queueFilter.val}
									onChange={(e) => setQueueFilter((f) => ({ ...f, val: e.target.value }))}
									sx={{
										width: 60,
										'& .MuiInputBase-input': { fontSize: 12, py: 0.5, px: 0.5 },
										'& .MuiOutlinedInput-notchedOutline': { border: 'none' },
									}}
								/>
								{active && (
									<Button
										size="small"
										onClick={() => setQueueFilter({ op: '>', val: '' })}
										sx={{ minWidth: 0, p: 0.2, fontSize: 11, color: 'text.secondary' }}
									>
										✕
									</Button>
								)}
							</Box>
						);
					})()}
					{/* Version filter */}
					{(() => {
						const active = versionFilter.apiVersion !== '' || versionFilter.lastVersion !== '';
						return (
							<Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid', borderColor: active ? 'primary.main' : 'divider', borderRadius: 1, px: 0.5 }}>
								<Typography variant="caption" sx={{ mr: 0.5, fontWeight: 'bold', color: active ? 'primary.main' : 'text.secondary' }}>
									version
								</Typography>
								<TextField
									size="small"
									placeholder="1.5.1"
									value={versionFilter.apiVersion}
									onChange={(e) => setVersionFilter((f) => ({ ...f, apiVersion: e.target.value }))}
									sx={{ width: 70, '& .MuiInputBase-input': { fontSize: 12, py: 0.5, px: 0.5 }, '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
								/>
								<Select
									size="small"
									value={versionFilter.lastVersion}
									onChange={(e) => setVersionFilter((f) => ({ ...f, lastVersion: e.target.value }))}
									displayEmpty
									sx={{ fontSize: 12, height: 28, '.MuiOutlinedInput-notchedOutline': { border: 'none' }, minWidth: 50 }}
								>
									<MenuItem value="">All</MenuItem>
									<MenuItem value="true">✓</MenuItem>
									<MenuItem value="false">✗</MenuItem>
								</Select>
								{active && (
									<Button size="small" onClick={() => setVersionFilter({ apiVersion: '', lastVersion: '' })} sx={{ minWidth: 0, p: 0.2, fontSize: 11, color: 'text.secondary' }}>
										✕
									</Button>
								)}
							</Box>
						);
					})()}
				</Box>
				{/* Time filters: last msg / last scan / last size — 2. satır */}
				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
					{TIME_FILTER_KEYS.map(({ key, label }) => {
						const f = timeFilters[key] || { op: '>', val: '', unit: 'h' };
						const active = f.val !== '';
						return (
							<Box
								key={key}
								sx={{
									display: 'flex',
									alignItems: 'center',
									border: '1px solid',
									borderColor: active ? 'primary.main' : 'divider',
									borderRadius: 1,
									px: 0.5,
								}}
							>
								<Typography variant="caption" sx={{ mr: 0.5, fontWeight: 'bold', color: active ? 'primary.main' : 'text.secondary', whiteSpace: 'nowrap' }}>
									{label}
								</Typography>
								<Select
									size="small"
									value={f.op}
									onChange={(e) => setTimeFilter(key, 'op', e.target.value)}
									sx={{ fontSize: 12, height: 28, '.MuiOutlinedInput-notchedOutline': { border: 'none' }, minWidth: 42 }}
								>
									<MenuItem value=">">{'>'}</MenuItem>
									<MenuItem value="<">{'<'}</MenuItem>
								</Select>
								<TextField
									size="small"
									placeholder="30"
									value={f.val}
									onChange={(e) => setTimeFilter(key, 'val', e.target.value)}
									sx={{
										width: 48,
										'& .MuiInputBase-input': { fontSize: 12, py: 0.5, px: 0.5 },
										'& .MuiOutlinedInput-notchedOutline': { border: 'none' },
									}}
								/>
								<Select
									size="small"
									value={f.unit || 'h'}
									onChange={(e) => setTimeFilter(key, 'unit', e.target.value)}
									sx={{ fontSize: 12, height: 28, '.MuiOutlinedInput-notchedOutline': { border: 'none' }, minWidth: 46 }}
								>
									<MenuItem value="m">min</MenuItem>
									<MenuItem value="h">hr</MenuItem>
									<MenuItem value="d">day</MenuItem>
								</Select>
								{active && (
									<Button size="small" onClick={() => clearTimeFilter(key)} sx={{ minWidth: 0, p: 0.2, fontSize: 11, color: 'text.secondary' }}>
										✕
									</Button>
								)}
							</Box>
						);
					})}
				</Box>
				</Box>

				{rows !== null && (
					<>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
							<Typography variant="caption" color="text.secondary">
								{displayed.length} / {rows.length} phones
							</Typography>
							<Box sx={{ flex: 1 }} />
							<Typography variant="caption" color="text.secondary">
								Rows per page:
							</Typography>
							<Select
								size="small"
								value={pageSize}
								onChange={(e) => {
									setPageSize(Number(e.target.value));
									setPage(0);
								}}
								sx={{ fontSize: 12, height: 28 }}
							>
								{[50, 100, 250, 500, 1000].map((n) => (
									<MenuItem key={n} value={n}>
										{n}
									</MenuItem>
								))}
							</Select>
							<Button size="small" disabled={page === 0} onClick={() => setPage(0)} sx={{ minWidth: 0, px: 1, fontSize: 11 }}>
								«
							</Button>
							<Button size="small" disabled={page === 0} onClick={() => setPage((p) => p - 1)} sx={{ minWidth: 0, px: 1, fontSize: 11 }}>
								‹
							</Button>
							<Typography variant="caption">
								{page + 1} / {totalPages || 1}
							</Typography>
							<Button
								size="small"
								disabled={page >= totalPages - 1}
								onClick={() => setPage((p) => p + 1)}
								sx={{ minWidth: 0, px: 1, fontSize: 11 }}
							>
								›
							</Button>
							<Button
								size="small"
								disabled={page >= totalPages - 1}
								onClick={() => setPage(totalPages - 1)}
								sx={{ minWidth: 0, px: 1, fontSize: 11 }}
							>
								»
							</Button>
						</Box>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 'bold', width: 40 }}>#</TableCell>
									<TableCell sx={{ fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => toggleSort('phoneId')}>
										Phone ID{' '}
										{sortKey === 'phoneId' &&
											(sortDir === 'desc' ? <ArrowDownwardIcon sx={{ fontSize: 12 }} /> : <ArrowUpwardIcon sx={{ fontSize: 12 }} />)}
									</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }}>Memory</TableCell>
									<TableCell sx={{ fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => toggleSort('total')}>
										Total{' '}
										{sortKey === 'total' &&
											(sortDir === 'desc' ? <ArrowDownwardIcon sx={{ fontSize: 12 }} /> : <ArrowUpwardIcon sx={{ fontSize: 12 }} />)}
									</TableCell>
									<TableCell sx={{ fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => toggleSort('sessionData')}>
										Session Data{' '}
										{sortKey === 'sessionData' &&
											(sortDir === 'desc' ? <ArrowDownwardIcon sx={{ fontSize: 12 }} /> : <ArrowUpwardIcon sx={{ fontSize: 12 }} />)}
									</TableCell>
									<TableCell sx={{ fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => toggleSort('other')}>
										Other{' '}
										{sortKey === 'other' &&
											(sortDir === 'desc' ? <ArrowDownwardIcon sx={{ fontSize: 12 }} /> : <ArrowUpwardIcon sx={{ fontSize: 12 }} />)}
									</TableCell>
									<TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Version</TableCell>
									<TableCell sx={{ fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => toggleSort('queue')}>
										Queue{' '}
										{sortKey === 'queue' &&
											(sortDir === 'desc' ? <ArrowDownwardIcon sx={{ fontSize: 12 }} /> : <ArrowUpwardIcon sx={{ fontSize: 12 }} />)}
									</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }}>Last Msg</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }}>Last Size</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }}>Last Scan</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{loading ? (
									<TableRow>
										<TableCell colSpan={12} align="center" sx={{ py: 4 }}>
											<CircularProgress size={28} />
										</TableCell>
									</TableRow>
								) : paged.length > 0 ? (
									paged.map((r, i) => (
										<PhoneRow
											key={r.phoneId}
											row={r}
											index={page * pageSize + i + 1}
											mem={memMap[r.phoneId]}
											queue={queueMap[r.phoneId]}
											queueLoading={queueLoading}
											lastMsg={lastMsgMap[r.phoneId]}
											lastMsgLoading={lastMsgLoading}
											lastScan={lastScanMap[r.phoneId]}
											lastScanLoading={lastScanLoading}
											version={versionMap[r.phoneId]}
											onClear={handleClear}
											pid={product && ((product.settings && product.settings.wapi_pid) || product.id)}
											token={product && product.settings && product.settings.wapi_token}
										/>
									))
								) : (
									<TableRow>
										<TableCell colSpan={12} align="center" sx={{ color: 'text.secondary', py: 3 }}>
											No data found
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</>
				)}
			</Box>
		</Page>
	);
}

export default connect((state) => ({ product: state.product.maytapi }))(InstanceSizesPage);
